import {
  type AddTrialBarItemRequest,
  AddTrialBarItemRequestSchema,
  type CockpitStateDto,
  CockpitStateDtoSchema,
  type CreateTrialBarRequest,
  type ReorderTrialBarItemsRequest,
  type ReorderTrialBarsRequest,
  type SaveCockpitStateRequest,
  type TrialBarDto,
  TrialBarDtoSchema,
  type TrialBarsResponse,
  TrialBarsResponseSchema,
  type UpdateTrialBarRequest,
} from '@ctms/contracts';

import { apiClient } from '../../api/client.js';
import { API_PATHS } from '../../api/paths.js';

/**
 * Cockpit personalization API (#7, Domain 4) — the user's favorite trial bars
 * and the last-trial recall. Every door is the calling user's own data, so no
 * bar/user id is ever passed from the client (the server scopes by session).
 * Zod schemas from `@ctms/contracts` are the single source of truth; each
 * response is parsed at the boundary so a malformed body fails loudly here.
 */
export const cockpitApi = {
  /** All bars of the calling user, with expanded active-trial items. */
  async listBars(): Promise<TrialBarsResponse> {
    const raw: unknown = await apiClient.get(API_PATHS.cockpit.trialBars).json();
    return TrialBarsResponseSchema.parse(raw);
  },

  /** Define a new bar; `position` is service-assigned (never sent). */
  async createBar(body: CreateTrialBarRequest): Promise<TrialBarDto> {
    const raw: unknown = await apiClient
      .post(API_PATHS.cockpit.trialBars, { json: body })
      .json();
    return TrialBarDtoSchema.parse(raw);
  },

  /** Rename / re-capacity a bar (`capacity: null` clears the numeric bound). */
  async updateBar(barId: number, body: UpdateTrialBarRequest): Promise<TrialBarDto> {
    const raw: unknown = await apiClient
      .patch(API_PATHS.cockpit.barById(barId), { json: body })
      .json();
    return TrialBarDtoSchema.parse(raw);
  },

  /** Delete a bar with its items (204, no body). */
  async deleteBar(barId: number): Promise<void> {
    await apiClient.delete(API_PATHS.cockpit.barById(barId));
  },

  /** Add a trial button; the server guards capacity/duplicate/inactive (409). */
  async addItem(barId: number, body: AddTrialBarItemRequest): Promise<TrialBarDto> {
    AddTrialBarItemRequestSchema.parse(body);
    const raw: unknown = await apiClient
      .post(API_PATHS.cockpit.barItems(barId), { json: body })
      .json();
    return TrialBarDtoSchema.parse(raw);
  },

  /** Remove one trial button; returns the fresh bar (200 with body). */
  async removeItem(barId: number, itemId: number): Promise<TrialBarDto> {
    const raw: unknown = await apiClient
      .delete(API_PATHS.cockpit.barItemById(barId, itemId))
      .json();
    return TrialBarDtoSchema.parse(raw);
  },

  /** Reorder ALL bars (full new order of ids). */
  async reorderBars(body: ReorderTrialBarsRequest): Promise<TrialBarsResponse> {
    const raw: unknown = await apiClient
      .put(API_PATHS.cockpit.reorderBars, { json: body })
      .json();
    return TrialBarsResponseSchema.parse(raw);
  },

  /** Reorder ALL items of one bar (full new order of item ids). */
  async reorderItems(barId: number, body: ReorderTrialBarItemsRequest): Promise<TrialBarDto> {
    const raw: unknown = await apiClient
      .put(API_PATHS.cockpit.reorderBarItems(barId), { json: body })
      .json();
    return TrialBarDtoSchema.parse(raw);
  },

  /** The remembered last active trial (`lastTrialId: null` = nothing to recall). */
  async getState(): Promise<CockpitStateDto> {
    const raw: unknown = await apiClient.get(API_PATHS.cockpit.state).json();
    return CockpitStateDtoSchema.parse(raw);
  },

  /** Remember the active trial (upsert; `null` clears the recall). */
  async saveState(body: SaveCockpitStateRequest): Promise<CockpitStateDto> {
    const raw: unknown = await apiClient.put(API_PATHS.cockpit.state, { json: body }).json();
    return CockpitStateDtoSchema.parse(raw);
  },
};
